import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(location.hash.slice(1));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/admin", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post(
          "/auth/profile",
          null,
          {
            headers: { "X-Session-ID": sessionId },
            withCredentials: true,
          }
        );
        setUser(data);
        toast.success(`Welcome, ${data.name || data.email}`);
        navigate("/admin", { replace: true, state: { user: data } });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        let msg;
        if (status === 403) {
          msg = "This Google account isn't authorized. Sign in with the owner email.";
        } else if (status === 401) {
          msg = "Sign-in expired. Please try again.";
        } else if (status) {
          msg = detail || `Sign-in failed (HTTP ${status})`;
        } else if (e?.message?.includes("Network")) {
          msg = "Couldn't reach the server. Check your connection and try again.";
        } else {
          msg = detail || e?.message || "Sign-in failed";
        }
        toast.error(msg, { duration: 8000 });
        navigate("/admin", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid place-items-center" data-testid="auth-callback">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
        Signing you in…
      </div>
    </div>
  );
}
